/**
 * CollectionDetail - Redesigned Collection Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Collapsible sections
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import {
	BaseForm,
	NumberBox,
	TextBox,
} from '../../components/base';
import { EditModal, EditPageSection, EditPageField } from '../../components/container';
import { ImageBox } from '../../components/base/ImageBox';
import { slugHelper } from '../../utils/slugHelper';
import { CollectionModel, dfCollectionModel, validCollectionSchema } from '../../context/models/collections/CollectionModel';
import ComboBoxCollectionGroup from '../../components/shared/hoc-collection-group/ComboBoxCollectionGroup';
import { Folder, Image as ImageIcon, Link as LinkIcon, Settings } from 'lucide-react';

type ICollectionDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: CollectionModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: CollectionModel) => void;
};

const CollectionDetail = (props: ICollectionDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<CollectionModel>(dfCollectionModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<CollectionModel>({
		resolver: yupResolver(validCollectionSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: CollectionModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	const watchChangeName = useWatch({ control, name: 'name', defaultValue: '' });

	useEffect(() => {
		const slug = slugHelper.toSlug(watchChangeName);
		setValue('slug', slug);
		setInitialValues((prev) => ({ ...prev, slug }));
	}, [watchChangeName, setValue]);

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof CollectionModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Basic Info
	const basicInfoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Nhóm bộ sưu tập'
				required
				error={errors.collectionGroupId?.message}
			>
				<ComboBoxCollectionGroup
					name='collectionGroupId'
					error={errors.collectionGroupId?.message}
					onValueChanged={(value) => updateField('collectionGroupId', value)}
					value={initialValues.collectionGroupId}
					hasValid={true}
					placeholder='Chọn nhóm bộ sưu tập...'
				/>
			</EditPageField>

			<EditPageField
				label='Tên bộ sưu tập'
				required
				error={errors.name?.message}
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên bộ sưu tập...'
				/>
			</EditPageField>

			<EditPageField
				label='Slug'
				error={errors.slug?.message}
				helpText='Link truy cập: ten-bo-suu-tap'
			>
				<TextBox
					name='slug'
					error={errors.slug?.message}
					onValueChanged={(value) => updateField('slug', value)}
					value={initialValues.slug}
					hasValid={true}
				/>
			</EditPageField>

			<EditPageField
				label='Mô tả'
				error={errors.description?.message}
			>
				<TextBox
					name='description'
					error={errors.description?.message}
					onValueChanged={(value) => updateField('description', value)}
					value={initialValues.description}
					hasValid={true}
					placeholder='Mô tả ngắn về bộ sưu tập...'
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Media
	const mediaTab = (
		<div className='space-y-6'>
			<EditPageField
				label='Hình ảnh đại diện'
				error={errors.image?.message}
				helpText='Hình ảnh hiển thị cho bộ sưu tập'
			>
				<ImageBox
					name='image'
					isDisabled={initialValues.name.length <= 0}
					file_name={initialValues.name}
					value={initialValues.image}
					onDataChanged={(image) => {
						updateField('image', image?.path || '');
					}}
				/>
			</EditPageField>

			<EditPageField
				label='Icon'
				error={errors.icon?.message}
				helpText='Icon thu nhỏ cho bộ sưu tập'
			>
				<ImageBox
					name='icon'
					isDisabled={initialValues.name.length <= 0}
					file_name={initialValues.name}
					value={initialValues.icon}
					onDataChanged={(icon) => {
						updateField('icon', icon?.path || '');
					}}
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Settings
	const settingsTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Liên kết'
				error={errors.link?.message}
				helpText='Đường dẫn liên kết đến trang khác (nếu có)'
			>
				<TextBox
					name='link'
					error={errors.link?.message}
					onValueChanged={(value) => updateField('link', value)}
					value={initialValues.link}
					hasValid={true}
					placeholder='https://...'
				/>
			</EditPageField>

			<EditPageField
				label='Số thứ tự'
				error={errors.stt?.message}
				helpText='Thứ tự hiển thị trong danh sách'
			>
				<NumberBox
					name='stt'
					error={errors.stt?.message}
					onValueChanged={(value) => updateField('stt', value)}
					value={initialValues.stt}
					hasValid={true}
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'basic',
			label: 'Thông tin cơ bản',
			icon: <Folder className='h-4 w-4' />,
			content: basicInfoTab,
		},
		{
			id: 'media',
			label: 'Hình ảnh',
			icon: <ImageIcon className='h-4 w-4' />,
			content: mediaTab,
		},
		{
			id: 'settings',
			label: 'Cài đặt',
			icon: <Settings className='h-4 w-4' />,
			content: settingsTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới bộ sưu tập' : 'Chỉnh sửa bộ sưu tập'}
			sizeClass='lg'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Bộ sưu tập', href: '/collections' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default CollectionDetail;
